import { Stack } from '@/components/layout';
import { AppHeader } from '@/components/layout/app-header';
import { PlayerListTemplateV2 } from '@/components/template/player-list';
import { SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { useCoachQuery } from '@/features/home/queries';
import { PlayerSummaryCard } from '@/features/player-session/components/session-summary/player-summary-card';
import { SessionSummaryStats } from '@/features/player-session/components/session-summary/session-summary-stats';
import {
  collapsibleOpenCache,
  scrollPageCache,
  summaryResetStore,
} from '@/features/player-session/components/session-summary/summary-card-cache-state';
import { useOverviewSessions } from '@/features/player-session/hooks/use-overview-sessions';
import { useRemoveCoachFromSessionQuery } from '@/features/player-session/queries';
import { playerSessionKeys } from '@/features/player-session/queries/query-keys';
import { createStyles, useScaling } from '@/hooks';
import { useDebounce } from '@/hooks/use-debounce';
import { captureException } from '@/libs/logger';
import { HomeStackParamList } from '@/navigations/types';
import { useSessionStore, useUiStore } from '@/stores';
import { colors } from '@/theme/colors';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';
import { useShallow } from 'zustand/shallow';

const renderItem = ({ item }: { item: playerSession.SessionLookupData }) => (
  <PlayerSummaryCard player={item} />
);

const SessionSummaryScreen = () => {
  const userUid = useSessionStore(s => s.user?.uid);

  useEffect(() => {
    return () => {
      collapsibleOpenCache.clear();
      scrollPageCache.clear();
    };
  }, []);

  return (
    <PlayerListTemplateV2.Providers userUid={userUid}>
      <SessionSummaryScreenContent />
    </PlayerListTemplateV2.Providers>
  );
};

const EndSessionButton = () => {
  const userUid = useSessionStore(useShallow(s => s.user?.uid));
  const { data: coach } = useCoachQuery(userUid);
  const { mutateAsync: removeCoachFromSession } = useRemoveCoachFromSessionQuery();
  const showGlobalLoading = useUiStore(useShallow(s => s.showGlobalLoading));
  const hideGlobalLoading = useUiStore(useShallow(s => s.hideGlobalLoading));
  const { scale, isTablet } = useScaling();
  const styles = useStyles();
  const navigation = useNavigation<NativeStackNavigationProp<HomeStackParamList>>();

  const onPress = useDebounce(
    useCallback(async () => {
      try {
        showGlobalLoading();
        await removeCoachFromSession({
          sessionId: coach?.active_session_id ?? '',
          coachId: coach?.id ?? '',
        });
        navigation.popTo('Dashboard');
      } catch (error) {
        captureException(
          {
            error_payload: error,
          },
          { tags: { usk_log_type: 'end_session_button' } },
        );
      } finally {
        hideGlobalLoading();
      }
    }, [
      showGlobalLoading,
      removeCoachFromSession,
      coach?.active_session_id,
      coach?.id,
      navigation,
      hideGlobalLoading,
    ]),
    1000,
    { leading: true },
  );

  return (
    <TouchableOpacity activeOpacity={0.7} onPress={onPress}>
      <Stack direction="row" justify="flex-end" align="center" gap={scale(6)} width={scale(50)}>
        <Text
          variant={isTablet ? 'Font Inter/Subtitle/Medium 14px' : 'Font Inter/Subtitle/Medium'}
          style={styles.backText}>
          {STRINGS.playerSession.sessionSummary.close}
        </Text>
        <SvgIcon name="IcClose" size={scale(12)} color={colors.Blue} />
      </Stack>
    </TouchableOpacity>
  );
};

function SessionSummaryScreenContent() {
  const { scale, isTablet } = useScaling();
  const styles = useStyles();
  const navigation = useNavigation<NativeStackNavigationProp<HomeStackParamList>>();
  const listPlayerLogic = useOverviewSessions({
    pagination: false,
    featureKey: playerSessionKeys.summaryList,
  });

  const totalSessionRoster = listPlayerLogic.paginatedMeta?.totalSessionRoster ?? 0;

  const summaryStats = useMemo(() => {
    const players = listPlayerLogic.listPlayer;
    const attended = listPlayerLogic.paginatedMeta?.totalSessionRoster ?? players.length;
    const clubsNeeded = listPlayerLogic.paginatedMeta?.totalNeedsClubs ?? 0;
    const pinsArchived = listPlayerLogic.paginatedMeta?.totalPinsArchived ?? 0;
    return { attended, clubsNeeded, pinsArchived };
  }, [
    listPlayerLogic.listPlayer,
    listPlayerLogic.paginatedMeta?.totalSessionRoster,
    listPlayerLogic.paginatedMeta?.totalNeedsClubs,
    listPlayerLogic.paginatedMeta?.totalPinsArchived,
  ]);

  const isFirstResetRef = useRef(true);
  useEffect(() => {
    if (isFirstResetRef.current) {
      isFirstResetRef.current = false;
      return;
    }
    summaryResetStore.reset();
  }, [
    listPlayerLogic.page,
    listPlayerLogic.searchText,
    listPlayerLogic.sort,
    listPlayerLogic.order,
    listPlayerLogic.level,
    listPlayerLogic.lastActive,
  ]);

  const onGoBack = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  return (
    <PlayerListTemplateV2.Root
      listLogic={listPlayerLogic}
      loadingText={STRINGS.player.listPlayers.loadingPlayers}
      skipHideActionButtonOnScroll>
      <PlayerListTemplateV2.Header>
        <AppHeader backgroundColor="white" style={styles.header}>
          <AppHeader.Left>
            <TouchableOpacity activeOpacity={0.6} onPress={onGoBack}>
              <Stack direction="row" align="center" gap={scale(2)}>
                <SvgIcon name="IcBackBlue" size={scale(24)} />
                <Text
                  variant={
                    isTablet ? 'Font Inter/Subtitle/Medium 14px' : 'Font Inter/Subtitle/Medium'
                  }
                  style={styles.backText}>
                  {STRINGS.playerSession.headerOverviewSession.back}
                </Text>
              </Stack>
            </TouchableOpacity>
          </AppHeader.Left>

          <AppHeader.Title>
            <Text
              variant={
                isTablet
                  ? 'Font Inter/Subtitle/Bold 14px'
                  : 'Font Montserrat/Subtitle/Subtitle 2 Bold 12px'
              }
              transform="uppercase"
              fontWeight={isTablet ? undefined : '800'}
              style={styles.headerTitle}>
              {STRINGS.playerSession.sessionSummary.title}
            </Text>
          </AppHeader.Title>

          <AppHeader.Right>
            <EndSessionButton />
          </AppHeader.Right>
        </AppHeader>
      </PlayerListTemplateV2.Header>

      <PlayerListTemplateV2.Toolbar
        titleResultsSummary={STRINGS.playerSession.headerOverviewSession.trackingPlayers(
          totalSessionRoster,
        )}
        isResultsSummaryVisible={false}>
        <PlayerListTemplateV2.SearchBar />
        <PlayerListTemplateV2.Filter />
        <PlayerListTemplateV2.Pagination />
        <SessionSummaryStats
          attended={summaryStats.attended}
          clubsNeeded={summaryStats.clubsNeeded}
          pinsArchived={summaryStats.pinsArchived}
        />
      </PlayerListTemplateV2.Toolbar>

      <PlayerListTemplateV2.List
        renderItem={renderItem}
        numberOfColumns={{ phone: 1, tablet: 2 }}
        gap={scale({ phone: 12, tablet: 16 })}
        emptyText={STRINGS.playerSession.headerOverviewSession.noPlayers}
        enableLazyLoadingList
        waitForInitialLayout
        // drawDistance={2000}
        useMasonry={isTablet}
        estimatedItemSize={scale(200)}
        extraData={listPlayerLogic.forceUpdateNonce}
      />
    </PlayerListTemplateV2.Root>
  );
}

export default SessionSummaryScreen;

const useStyles = createStyles(() => ({
  header: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors['10'],
  },
  headerTitle: {
    color: colors.Blue,
    width: '100%',
    textAlign: 'center',
  },
  backText: {
    color: colors.Blue,
  },
  slideLabel: {
    color: colors['10'],
  },
}));
